import { useState } from "react";
import { View } from "react-native";
import { SvgXml } from "react-native-svg";

import { AppText, Button, ButtonText, Card, TextField } from "~/components/ui";
import {
  disableTotp,
  startTotpSetup,
  useTotpStatus,
  verifyTotpCode,
  type TotpSetup,
} from "~/lib/totp";

/** Account settings section: turn optional two-factor authentication on or off. */
export function TwoFactorCard() {
  const status = useTotpStatus();
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  };

  const begin = () =>
    run(async () => setSetup(await startTotpSetup()));
  const confirm = () =>
    run(async () => {
      setRecoveryCodes(await verifyTotpCode(code));
      setSetup(null);
      setCode("");
      await status.refresh();
    });
  const turnOff = () =>
    run(async () => {
      await disableTotp();
      setRecoveryCodes([]);
      await status.refresh();
    });
  const cancel = () => {
    setSetup(null);
    setCode("");
    setError("");
  };

  return (
    <Card gap="sm">
      <AppText weight="semibold">Two-factor authentication</AppText>
      {status.loading ? (
        <AppText size="xs">Checking…</AppText>
      ) : setup ? (
        <>
          <AppText size="xs">
            Scan this code with an authenticator app, or type the key in by
            hand. Then enter the 6-digit code it shows.
          </AppText>
          <View style={{ alignSelf: "center", width: 200, height: 200 }}>
            <SvgXml xml={setup.qrSvg} width="100%" height="100%" />
          </View>
          <AppText weight="medium">Setup key</AppText>
          <TextField
            accessibilityLabel="Setup key"
            value={setup.secret}
            editable={false}
            muted
            selectTextOnFocus
          />
          <AppText weight="medium">6-digit code</AppText>
          <TextField
            accessibilityLabel="6-digit code"
            value={code}
            onChangeText={setCode}
            keyboardType="number-pad"
            autoComplete="one-time-code"
            maxLength={6}
            centered
          />
          {error ? (
            <AppText tone="danger" size="xs">
              {error}
            </AppText>
          ) : null}
          <Button
            disabled={busy || code.trim().length !== 6}
            onPress={() => void confirm()}
          >
            <ButtonText>{busy ? "Checking…" : "Turn on two-factor"}</ButtonText>
          </Button>
          <Button variant="outline" onPress={cancel}>
            <ButtonText>Cancel</ButtonText>
          </Button>
        </>
      ) : status.enabled ? (
        <>
          <AppText size="xs">
            On. Signing in asks for a code from your authenticator app.
          </AppText>
          {recoveryCodes.length ? (
            <>
              <AppText size="xs" weight="medium">
                Save these recovery codes somewhere safe. Each works once if you
                lose your authenticator, and they will not be shown again.
              </AppText>
              <TextField
                accessibilityLabel="Recovery codes"
                value={recoveryCodes.join("  ")}
                editable={false}
                muted
                multiline
                selectTextOnFocus
              />
              <Button variant="outline" onPress={() => setRecoveryCodes([])}>
                <ButtonText>I have saved them</ButtonText>
              </Button>
            </>
          ) : null}
          {error ? (
            <AppText tone="danger" size="xs">
              {error}
            </AppText>
          ) : null}
          <Button
            variant="danger"
            disabled={busy}
            onPress={() => void turnOff()}
          >
            <ButtonText tone="danger">
              {busy ? "Working…" : "Turn off two-factor"}
            </ButtonText>
          </Button>
        </>
      ) : (
        <>
          <AppText size="xs">
            Optional. Add a code from an authenticator app to sign-in for extra
            protection.
          </AppText>
          {error ? (
            <AppText tone="danger" size="xs">
              {error}
            </AppText>
          ) : null}
          <Button
            variant="outline"
            disabled={busy}
            onPress={() => void begin()}
          >
            <ButtonText>{busy ? "Working…" : "Set up two-factor"}</ButtonText>
          </Button>
        </>
      )}
    </Card>
  );
}
